import React from 'react';
import {
  Play,
  Sun,
  Coffee,
  Moon,
  Timer,
  Heart,
  ChevronRight,
  AlertCircle,
  Footprints,
  Dog,
  Crown,
  AlertTriangle
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const EjercicioCard = ({ ejercicioYDiversion }) => {
  if (!ejercicioYDiversion) return null;

  /**
   * Retorna el ícono según el periodo indicado
   */
  const getPeriodIcon = (period) => {
    switch (period) {
      case 'mañana':
        return Sun;
      case 'mediodia':
        return Coffee;
      case 'tarde':
        return Moon;
      default:
        return Play;
    }
  };

  /**
   * Subcomponente para cada tarjeta de actividad
   */
  const ActivityCard = ({ data, period }) => {
    const Icon = getPeriodIcon(period);

    return (
      <div className="bg-gradient-to-br from-indigo-50 to-white rounded-lg border border-indigo-100 hover:shadow-sm transition-all">
        <div className="p-4 space-y-4">
          {/* Header: título y duración */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Icon className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h4 className="text-base sm:text-lg font-medium text-gray-900">
                  {data.actividad}
                </h4>
                {data.duracion && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <Timer className="h-4 w-4 text-indigo-500" />
                    <span className="text-sm text-gray-600">
                      {data.duracion}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recomendaciones, consejos, ideas */}
          {(data.recomendaciones || data.consejos || data.ideas) && (
            <div className="space-y-2">
              {/* Recomendaciones */}
              {data.recomendaciones?.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 bg-white p-3 rounded-lg group hover:shadow-sm transition-all"
                >
                  <Crown className="h-4 w-4 mt-0.5 text-indigo-500" />
                  <p className="text-base text-gray-700">{item}</p>
                </div>
              ))}

              {/* Consejos */}
              {data.consejos?.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 bg-white p-3 rounded-lg group hover:shadow-sm transition-all"
                >
                  <ChevronRight className="h-4 w-4 mt-0.5 text-indigo-500 group-hover:translate-x-0.5 transition-transform" />
                  <p className="text-base text-gray-700">{item}</p>
                </div>
              ))}

              {/* Ideas de juego */}
              {data.ideas?.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 bg-white p-3 rounded-lg group hover:shadow-sm transition-all"
                >
                  <Play className="h-4 w-4 mt-0.5 text-indigo-500" />
                  <p className="text-base text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="overflow-hidden bg-white">
      {/* Encabezado del Card */}
      <CardHeader className="border-b bg-gradient-to-r from-indigo-500/10 to-transparent p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-sm">
            <Dog className="h-5 w-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl font-semibold text-gray-800">
              Ejercicio y Diversión
            </CardTitle>
            <p className="text-base text-gray-500 mt-1">
              Actividades diarias y señales de descanso
            </p>
          </div>
        </div>
      </CardHeader>

      {/* Contenido del Card */}
      <CardContent className="p-4 space-y-4">
        {/* Plan de Actividades */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Footprints className="h-5 w-5 text-indigo-500" />
            <h3 className="text-lg font-medium text-gray-900">
              Plan de Actividades
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(ejercicioYDiversion.actividadesDiarias).map(
              ([period, data]) => (
                <ActivityCard key={period} data={data} period={period} />
              )
            )}
          </div>
        </div>

        {/* Señales de Descanso */}
        {ejercicioYDiversion.señalesDeDescanso && (
          <div className="bg-orange-50 rounded-lg border border-orange-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              <h3 className="text-lg font-medium text-gray-900">
                Señales de Descanso
              </h3>
            </div>
            <div className="space-y-3">
              {ejercicioYDiversion.señalesDeDescanso.map((señal, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 bg-white p-3 rounded-lg"
                >
                  <AlertCircle className="h-4 w-4 mt-0.5 text-orange-500" />
                  <p className="text-base text-gray-700">{señal}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EjercicioCard;
